import React, { useState } from 'react'
import { Button } from 'rsuite'
import Icon from './Icon'
import Divider from './Divider'
import KioskOptions, { useManualZoom } from './KioskOptions'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { getKioskOptions } from '../redux/options/selectors'
import { setKioskOption } from '../redux/options/actions'
import { getDataContest } from '../redux/data/selectors'

// tools
import { getContestDay } from '../tools/Weather'
import { formatDate } from '../tools/Time'
import { ZOOM, OPTION_CHARTS, OPTION_WEATHER, getZoom, getShowCharts, getShowWeather, getAutoFit, stepZoom, formatZoom } from '../tools/Kiosk'

/**
 * Buttons in the bottom right corner of the kiosk screen: fit to the screen and quick zoom, the
 * charts and the weather on or off, and the kiosk options, which open just above the buttons. Only
 * the options until hovered, with the day of the shown race next to them, which the open buttons
 * cover
 * @param {Object} props component props
 * @param {Function} props.onFit called to zoom so that everything fits the screen, also later on
 * @returns {JSX.Element} buttons with the options
 */
const KioskControls = ( { onFit } ) =>
{
  const options = useSelector( getKioskOptions )
  const dispatch = useDispatch()
  const zoom = getZoom( options )
  const charts = getShowCharts( options )
  const weather = getShowWeather( options )
  const autoFit = getAutoFit( options )
  const setZoom = useManualZoom()
  const [ showOptions, setShowOptions ] = useState( false )
  const day = getContestDay( useSelector( getDataContest ) )

  /**
   * Zoom in or out by a quick step
   * @param {number} steps 1 to zoom in, -1 to zoom out
   * @returns {void}
   */
  const onZoom = ( steps ) => setZoom( stepZoom( zoom, steps ) )

  // the toggle buttons say what a click does
  const chartsLabel = charts ? 'Hide position charts' : 'Show position charts'
  const weatherLabel = weather ? 'Hide weather' : 'Show weather'

  // only the options button until the bar is hovered or focused, or the options are open; then the
  // buttons slide open to the left over the day of the race, see Kiosk.less
  return (
    <React.Fragment>
      { day && <span className="kiosk-day"><Icon icon="calendar"/>{ formatDate( day, true ) }</span> }
      <div className={ showOptions ? 'kiosk-controls is-open' : 'kiosk-controls' }>
        { /* above the bar, inside it so the bar stays open while the options are used */ }
        <KioskOptions show={ showOptions } onClose={ () => setShowOptions( false ) }/>
        <div className="kiosk-controls-buttons">
          <span className="kiosk-controls-more">
            <span className="kiosk-controls-items">
              <Button
                className="kiosk-fit"
                appearance={ autoFit ? 'primary' : 'default' }
                aria-pressed={ autoFit }
                aria-label="Fit to the screen"
                title={ autoFit ? 'Fits to the screen; zoom to stop' : 'Fit to the screen' }
                onClick={ onFit }
              ><Icon icon="zoom"/></Button>
              <span className="kiosk-zoom">
                <Button
                  aria-label={ `Zoom out (${ formatZoom( zoom ) })` }
                  title={ `Zoom out (${ formatZoom( zoom ) })` }
                  disabled={ zoom <= ZOOM.min }
                  onClick={ () => onZoom( -1 ) }
                ><Icon icon="minus"/></Button>
                <span className="kiosk-zoom-value">{ formatZoom( zoom ) }</span>
                <Button
                  aria-label={ `Zoom in (${ formatZoom( zoom ) })` }
                  title={ `Zoom in (${ formatZoom( zoom ) })` }
                  disabled={ zoom >= ZOOM.max }
                  onClick={ () => onZoom( 1 ) }
                ><Icon icon="plus"/></Button>
              </span>
              <Divider/>
              <Button
                appearance={ charts ? 'primary' : 'default' }
                aria-pressed={ charts }
                aria-label={ chartsLabel }
                title={ chartsLabel }
                onClick={ () => dispatch( setKioskOption( OPTION_CHARTS, !charts ) ) }
              ><Icon icon="chart"/></Button>
              <Button
                appearance={ weather ? 'primary' : 'default' }
                aria-pressed={ weather }
                aria-label={ weatherLabel }
                title={ weatherLabel }
                onClick={ () => dispatch( setKioskOption( OPTION_WEATHER, !weather ) ) }
              ><Icon icon="weather-partly"/></Button>
            </span>
          </span>
          <Button
            appearance={ showOptions ? 'primary' : 'default' }
            aria-expanded={ showOptions }
            aria-label="Kiosk options"
            title="Kiosk options"
            onClick={ () => setShowOptions( !showOptions ) }
          ><Icon icon="cog"/></Button>
        </div>
      </div>
    </React.Fragment>
  )
}

export default KioskControls
